import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, Wand2, Loader2, Sparkles, Play, PencilRuler, RotateCcw, Bug,
  CheckCircle2, Circle, XCircle, MinusCircle, GripVertical, ChevronUp, ChevronDown,
  Coins, Swords, Layers, Cog, Globe2, BookOpen, Eye, History, ArrowLeftRight,
  Share2, Dices, Mic, MicOff, Compass, Save,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import useSpeech from "@/lib/useSpeech";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Spinner } from "@/components/Bits";
import GameCanvas from "@/components/GameCanvas";
import { toast } from "sonner";

export const THEME_PACKS = [
  { id: "halloween", name: "Halloween", emoji: "🎃", grad: "from-[#7c2d12] to-[#b45309]", goal: "Give the whole game a spooky Halloween makeover: an eerie purple-and-orange world, drifting fog, friendly ghost enemies and a mischievous pumpkin boss" },
  { id: "winter", name: "Winter", emoji: "❄️", grad: "from-[#0e7490] to-[#38bdf8]", goal: "Turn it into a frosty Winter Wonderland: a snowy world, an icy-blue palette, snowball power-ups and a big friendly snow-golem boss" },
  { id: "cyberpunk", name: "Cyberpunk", emoji: "🌆", grad: "from-[#701a75] to-[#22d3ee]", goal: "Reskin it as a neon cyberpunk night city: a glowing magenta-and-cyan world, hovering robot enemies and a synth-powered boss" },
  { id: "underwater", name: "Underwater", emoji: "🌊", grad: "from-[#155e75] to-[#2563eb]", goal: "Make it an underwater adventure: an ocean-blue world with bubbly ambience, playful fish enemies and a giant friendly kraken boss" },
  { id: "volcano", name: "Volcano", emoji: "🌋", grad: "from-[#7f1d1d] to-[#ea580c]", goal: "Give it a fiery volcano theme: lava hazards, drifting embers, a molten palette and a rock-golem boss" },
  { id: "candy", name: "Candy Land", emoji: "🍭", grad: "from-[#be185d] to-[#f472b6]", goal: "Turn it into a sweet Candy Land: a pastel world, lollipop collectibles, gummy-bear enemies and a giant cupcake boss" },
  { id: "space", name: "Deep Space", emoji: "🚀", grad: "from-[#1e1b4b] to-[#6d28d9]", goal: "Blast it into deep space: a starry skybox, a low-gravity feel, alien enemies and a spinning UFO boss" },
  { id: "jungle", name: "Jungle", emoji: "🌴", grad: "from-[#14532d] to-[#65a30d]", goal: "Reskin it as a lush jungle expedition: dense foliage, vine ambience, cheeky monkey enemies and a giant plant boss" },
  { id: "arcade", name: "Retro Arcade", emoji: "🕹️", grad: "from-[#3730a3] to-[#db2777]", goal: "Make it a retro arcade throwback: a bright toon look, a combo score multiplier, fun power-ups and a fast score chase" },
];

const CATS = {
  economy: { Icon: Coins, color: "#f59e0b", label: "Economy" },
  enemies: { Icon: Swords, color: "#f43f5e", label: "Enemies" },
  levels: { Icon: Layers, color: "#3b9eff", label: "Levels" },
  mechanics: { Icon: Cog, color: "#a855f7", label: "Mechanics" },
  world: { Icon: Globe2, color: "#14b8a6", label: "World" },
  narrative: { Icon: BookOpen, color: "#6366f1", label: "Narrative" },
  polish: { Icon: Sparkles, color: "#ec4899", label: "Polish" },
};
const catOf = (c) => CATS[c] || CATS.mechanics;

const EXAMPLES = [
  "Turn this into a roguelike with a 3-phase boss, a coin shop, and permadeath",
  "Add three escalating levels with tougher enemies and a final boss",
  "Give it an RPG layer: quests, an NPC shopkeeper, and collectible upgrades",
  "Make it a fast arcade score-attack with combos, power-ups and a timer",
];

export default function AutopilotStudio() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [game, setGame] = useState(null);
  const [config, setConfig] = useState(null);
  const [goal, setGoal] = useState("");
  const [phase, setPhase] = useState("idle");        // idle | planning | plan | running | done
  const [overview, setOverview] = useState("");
  const [planId, setPlanId] = useState(null);
  const [steps, setSteps] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [autoQa, setAutoQa] = useState(true);
  const [qa, setQa] = useState(null);                // null | {status,overall,verdict}
  const [starters, setStarters] = useState([]);
  const [recentPlans, setRecentPlans] = useState([]);
  const [preview, setPreview] = useState(null);      // {step, data|null, side}
  const [applying, setApplying] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [surpriseBusy, setSurpriseBusy] = useState(false);
  const [packsOpen, setPacksOpen] = useState(false);
  const [sharedFrom, setSharedFrom] = useState(null);
  const [adoptBusy, setAdoptBusy] = useState(false);
  const qaPoll = useRef(null);
  const autoGoalDone = useRef(false);
  const autoPlanDone = useRef(false);
  const { listening, supported: micSupported, start: micStart, stop: micStop } =
    useSpeech((t) => setGoal((p) => (p ? p + " " : "") + t));

  useEffect(() => {
    api.get(`/games/${gameId}`).then(({ data }) => {
      setGame(data);
      setConfig(data.config);
    }).catch(() => toast.error("Couldn't open this game."));
    api.get(`/games/${gameId}/autopilot/starters`).then(({ data }) => setStarters(data.starters || [])).catch(() => {});
    api.get(`/autopilot/recent-plans`).then(({ data }) => setRecentPlans(data.plans || [])).catch(() => {});
    return () => clearInterval(qaPoll.current);
  }, [gameId]);

  const draftPlan = useCallback(async (goalText) => {
    const g = (goalText ?? goal).trim();
    if (g.length < 4) { toast.error("Tell the Director what you want to build."); return; }
    setPhase("planning");
    setSteps([]); setOverview(""); setQa(null); setSharedFrom(null);
    try {
      const { data } = await api.post(`/games/${gameId}/autopilot/plan`, { goal: g }, { timeout: 120000 });
      setPlanId(data.plan_id);
      setOverview(data.overview);
      setSteps((data.steps || []).map((s) => ({ ...s, include: true, status: "pending", summary: "", version: null })));
      setPhase("plan");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "The Director couldn't draft a plan.");
      setPhase("idle");
    }
  }, [goal, gameId]);

  // Hand-off from the builder chat: /app/autopilot/:id?goal=... prefills and auto-drafts.
  useEffect(() => {
    const g = searchParams.get("goal");
    if (g && game && !autoGoalDone.current) {
      autoGoalDone.current = true;
      setGoal(g);
      draftPlan(g);
    }
  }, [searchParams, game, draftPlan]);

  // Shared plan hand-off: /app/autopilot/:id?plan=<code> loads a friend's plan to run on THIS game.
  useEffect(() => {
    const code = searchParams.get("plan");
    if (code && game && !autoPlanDone.current) {
      autoPlanDone.current = true;
      api.get(`/autopilot/shared/${code}`).then(({ data }) => {
        setGoal(data.goal || "");
        setOverview(data.overview || "");
        setSteps((data.steps || []).map((s) => ({ ...s, include: true, status: "pending", summary: "", version: null })));
        setPlanId(null);
        setSharedFrom(data.game_title || "a creator");
        setPhase("plan");
        api.post(`/autopilot/shared/${code}/remix`).catch(() => {});
        toast.success(`Loaded a shared plan from ${data.game_title} — run it on this game! 🎁`);
      }).catch(() => toast.error("That shared plan link is invalid or expired."));
    }
  }, [searchParams, game]);

  const surpriseMe = async () => {
    if (surpriseBusy || busy) return;
    setPacksOpen(false);
    setSurpriseBusy(true);
    try {
      const { data } = await api.get(`/games/${gameId}/autopilot/surprise`);
      setGoal(data.goal || "");
      await draftPlan(data.goal);
    } catch (e) {
      toast.error("Couldn't dream one up — type a goal instead.");
    } finally { setSurpriseBusy(false); }
  };

  const pickPack = (p) => {
    setPacksOpen(false);
    setGoal(p.goal);
    draftPlan(p.goal);
  };

  const adoptPlan = async () => {
    if (adoptBusy) return;
    setAdoptBusy(true);
    try {
      const cleanSteps = steps.map(({ id, title, category, rationale, instruction }) => ({ id, title, category, rationale, instruction }));
      const { data } = await api.post(`/games/${gameId}/autopilot/adopt`, { goal, overview, steps: cleanSteps });
      setPlanId(data.plan_id);
      setSharedFrom(null);
      toast.success("Saved to your plans — tweak the steps and re-share it! ✨");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save this plan.");
    } finally { setAdoptBusy(false); }
  };

  const sharePlan = async () => {
    if (!planId) { toast.error("Draft a plan first, then share it."); return; }
    setShareBusy(true);
    try {
      const { data } = await api.post(`/games/${gameId}/autopilot/${planId}/share`);
      const link = `${window.location.origin}/app/plan/${data.code}`;
      try {
        await navigator.clipboard.writeText(link);
        toast.success("Share link copied! Friends can run this exact plan on their own game. 🔗");
      } catch {
        window.prompt("Copy this plan link:", link);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't create a share link.");
    } finally { setShareBusy(false); }
  };

  const reusePlan = (p) => {
    setGoal(p.goal || "");
    setOverview(p.overview || "");
    setSteps((p.steps || []).map((s) => ({ ...s, include: true, status: "pending", summary: "", version: null })));
    setPlanId(p.id || null);   // keep the link so this reused plan can be shared/re-run
    setSharedFrom(null);
    setQa(null);
    setPhase("plan");
    toast.success("Plan loaded — tweak, run, or share it.");
  };

  const move = (idx, dir) => {
    setSteps((prev) => {
      const arr = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return prev;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };
  const toggleInclude = (id) =>
    setSteps((prev) => prev.map((s) => (s.id === id ? { ...s, include: !s.include } : s)));

  const openPreview = async (step) => {
    setPreview({ step, data: null, side: "after" });
    try {
      const { data } = await api.post(`/games/${gameId}/autopilot/preview`,
        { instruction: step.instruction }, { timeout: 90000 });
      setPreview({ step, data, side: "after" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't preview that step.");
      setPreview(null);
    }
  };

  const applyPreview = async () => {
    if (!preview?.data) return;
    setApplying(true);
    try {
      const { data } = await api.post(`/games/${gameId}/autopilot/apply`, {
        config: preview.data.after, summary: preview.data.summary,
        plan_id: planId, step_id: preview.step.id,
      }, { timeout: 30000 });
      if (data.config) setConfig(data.config);
      setSteps((prev) => prev.map((x) => (x.id === preview.step.id
        ? { ...x, status: "done", summary: preview.data.summary, version: data.version, include: false }
        : x)));
      toast.success(`Applied — saved as v${data.version}.`);
      setPreview(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't apply that step.");
    } finally { setApplying(false); }
  };

  const runQa = useCallback(async () => {
    setQa({ status: "processing", overall: 0, verdict: "" });
    try {
      const { data } = await api.post(`/qa/${gameId}/run`, {}, { timeout: 40000 });
      setQa(data);
      if (data.status === "processing") {
        qaPoll.current = setInterval(async () => {
          try {
            const { data: d } = await api.get(`/qa/${gameId}`);
            if (d?.latest) {
              setQa(d.latest);
              if (d.latest.status === "ready") clearInterval(qaPoll.current);
            }
          } catch { /* keep polling */ }
        }, 4000);
      }
    } catch (e) {
      setQa(null);
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't run the playtest.");
    }
  }, [gameId]);

  const runAutopilot = async () => {
    const included = steps.filter((s) => s.include);
    if (included.length === 0) { toast.error("Keep at least one step to run."); return; }
    setPhase("running");
    let ok = 0, failed = 0, stopped = false;
    for (const s of included) {
      setCurrentId(s.id);
      setSteps((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: "running" } : x)));
      try {
        const { data } = await api.post(`/games/${gameId}/autopilot/step`,
          { instruction: s.instruction, plan_id: planId, step_id: s.id }, { timeout: 90000 });
        if (data.config) setConfig(data.config);
        if (data.changed) ok += 1;
        setSteps((prev) => prev.map((x) => (x.id === s.id
          ? { ...x, status: data.changed ? "done" : "nochange", summary: data.summary, version: data.version }
          : x)));
      } catch (e) {
        failed += 1;
        const msg = formatApiError(e.response?.data?.detail) || "Step failed.";
        setSteps((prev) => prev.map((x) => (x.id === s.id ? { ...x, status: "failed", summary: msg } : x)));
        if (e.response?.status === 402) { toast.error("Out of AI credits — top up to finish the plan."); stopped = true; break; }
      }
    }
    setCurrentId(null);
    setPhase("done");
    if (planId) { try { await api.post(`/games/${gameId}/autopilot/${planId}/finish`); } catch { /* non-blocking */ } }
    if (ok === 0) toast.error("None of the steps could be applied — try a different goal.");
    else if (failed > 0 || stopped) toast.success(`Autopilot done — ${ok} applied, ${failed} skipped.`);
    else toast.success("🎬 Autopilot finished — your game just leveled up!");
    if (autoQa && ok > 0) runQa();
  };

  const reset = () => {
    setPhase("idle"); setSteps([]); setOverview(""); setPlanId(null); setQa(null); setGoal(""); setSharedFrom(null);
    clearInterval(qaPoll.current);
  };

  const includedCount = steps.filter((s) => s.include).length;
  const doneCount = steps.filter((s) => ["done", "nochange", "failed"].includes(s.status)).length;
  const busy = phase === "planning" || phase === "running";

  if (!game) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="autopilot-studio">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="autopilot-back">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="w-11 h-11 rounded-2xl grid place-items-center bg-gradient-to-br from-[#1f6feb] to-[#a855f7] shrink-0">
          <Wand2 className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <h1 className="font-display text-xl font-extrabold leading-tight truncate">AI Game Director</h1>
          <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">
            Describe a big goal — the Director plans it and builds it, step by step, on <b>{game.title}</b>.
          </p>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        {/* Left: goal / plan / progress */}
        <div className="space-y-5">
          {/* Goal input */}
          {(phase === "idle" || phase === "planning") && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
              <label className="text-sm font-semibold">What should we build?</label>
              <div className="relative mt-2">
                <Textarea
                  value={goal} onChange={(e) => setGoal(e.target.value)} rows={3}
                  placeholder="e.g. Turn this into a roguelike with a 3-phase boss, a coin shop for upgrades, and three escalating levels…"
                  className="resize-none pr-11" data-testid="autopilot-goal-input" readOnly={phase === "planning"} />
                {micSupported && (
                  <button type="button" onClick={listening ? micStop : micStart} disabled={phase === "planning"}
                    title={listening ? "Stop listening" : "Speak your goal"} data-testid="autopilot-goal-mic"
                    className={`absolute top-2 right-2 w-8 h-8 grid place-items-center rounded-full transition-colors ${listening ? "bg-rose-500 text-white animate-pulse" : "bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]"}`}>
                    {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
                  </button>
                )}
              </div>
              {listening && <p className="text-[11px] text-rose-500 mt-1.5 flex items-center gap-1"><Mic className="w-3 h-3" /> Listening… say your big goal out loud.</p>}
              <div className="flex flex-wrap gap-1.5 mt-3">
                {(starters.length ? starters : EXAMPLES).map((ex, i) => (
                  <button key={i} onClick={() => setGoal(ex)} disabled={phase === "planning"}
                    className="text-[11px] rounded-full border border-[hsl(var(--border))] px-2.5 py-1 text-[hsl(var(--muted-foreground))] hover:border-[hsl(var(--primary))]/50 hover:text-[hsl(var(--foreground))] transition-colors"
                    data-testid={`autopilot-example-${i}`}>
                    {ex.length > 46 ? ex.slice(0, 44) + "…" : ex}
                  </button>
                ))}
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={() => draftPlan()} disabled={busy || surpriseBusy} className="rounded-full flex-1 bg-gradient-to-r from-[#1f6feb] to-[#a855f7]" data-testid="autopilot-plan-btn">
                  {phase === "planning" && !surpriseBusy ? <><Loader2 className="w-4 h-4 animate-spin" /> Drafting your build plan…</> : <><Sparkles className="w-4 h-4" /> Draft build plan <span className="opacity-70 text-xs">· 2 credits</span></>}
                </Button>
                <Popover open={packsOpen} onOpenChange={setPacksOpen}>
                  <PopoverTrigger asChild>
                    <Button variant="outline" disabled={busy || surpriseBusy} className="rounded-full shrink-0" title="Pick a fun themed makeover" data-testid="autopilot-surprise-btn">
                      {surpriseBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Dices className="w-4 h-4" />} Surprise me
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" side="top" sideOffset={8} className="w-72 p-2" data-testid="autopilot-packs">
                    <p className="text-xs font-semibold px-1 pb-1.5 text-[hsl(var(--muted-foreground))]">Pick a themed makeover</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      {THEME_PACKS.map((p) => (
                        <button key={p.id} onClick={() => pickPack(p)} data-testid={`autopilot-pack-${p.id}`}
                          className={`rounded-xl bg-gradient-to-br ${p.grad} p-2 text-white text-center hover:scale-[1.04] active:scale-95 transition-transform`}>
                          <div className="text-2xl leading-none">{p.emoji}</div>
                          <div className="text-[10px] font-semibold mt-1 leading-tight">{p.name}</div>
                        </button>
                      ))}
                      <button onClick={surpriseMe} data-testid="autopilot-pack-random"
                        className="rounded-xl border border-dashed border-[hsl(var(--border))] p-2 text-center hover:border-[hsl(var(--primary))]/60 hover:bg-[hsl(var(--primary))]/5 transition-colors">
                        <div className="text-2xl leading-none">🎲</div>
                        <div className="text-[10px] font-semibold mt-1 leading-tight">Random</div>
                      </button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <button onClick={() => navigate("/app/gallery")} data-testid="autopilot-gallery-link"
                className="mt-3 inline-flex items-center gap-1 text-xs text-[hsl(var(--primary))] hover:underline font-medium">
                <Compass className="w-3.5 h-3.5" /> Browse the community plan gallery
              </button>
            </div>
          )}

          {/* Reuse a past plan (one-tap rerun, same or different game) */}
          {phase === "idle" && recentPlans.length > 0 && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5" data-testid="autopilot-recent-plans">
              <div className="flex items-center gap-2 mb-3 text-sm font-semibold"><History className="w-4 h-4 text-[hsl(var(--primary))]" /> Reuse a past plan</div>
              <div className="space-y-2">
                {recentPlans.map((p) => (
                  <div key={p.id} className="flex items-center gap-3 rounded-xl border border-[hsl(var(--border))] px-3 py-2" data-testid={`autopilot-recent-${p.id}`}>
                    <span className="text-lg shrink-0">{p.cover_emoji}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium leading-snug truncate">{p.goal}</p>
                      <p className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">
                        {p.step_count} steps · from {p.game_title}
                      </p>
                    </div>
                    <Button size="sm" variant="outline" className="rounded-full shrink-0 text-xs" onClick={() => reusePlan(p)} data-testid={`autopilot-reuse-${p.id}`}>
                      <RotateCcw className="w-3.5 h-3.5" /> Use
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Plan / progress */}
          {(phase === "plan" || phase === "running" || phase === "done") && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-5">
              <div className="flex items-start gap-2 mb-4">
                <Sparkles className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
                <p className="text-sm font-medium leading-snug" data-testid="autopilot-overview">{overview}</p>
              </div>

              {phase === "running" && (
                <div className="mb-4" data-testid="autopilot-progress">
                  <div className="h-1.5 rounded-full bg-[hsl(var(--secondary))] overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#1f6feb] to-[#a855f7] transition-all duration-500"
                      style={{ width: `${includedCount ? (doneCount / includedCount) * 100 : 0}%` }} />
                  </div>
                  <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5">Building… {doneCount} / {includedCount} steps</p>
                </div>
              )}

              <div className="space-y-2" data-testid="autopilot-steps">
                {steps.map((s, i) => {
                  const cat = catOf(s.category);
                  const Icon = cat.Icon;
                  const active = currentId === s.id;
                  return (
                    <div key={s.id}
                      className={`rounded-xl border p-3 transition-colors ${active ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5" : "border-[hsl(var(--border))]"} ${s.include ? "" : "opacity-50"}`}
                      data-testid={`autopilot-step-${i}`}>
                      <div className="flex items-start gap-2.5">
                        <div className="grid place-items-center w-8 h-8 rounded-lg shrink-0 mt-0.5" style={{ background: `${cat.color}22` }}>
                          {s.status === "running" ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: cat.color }} />
                            : s.status === "done" ? <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                              : s.status === "nochange" ? <MinusCircle className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                                : s.status === "failed" ? <XCircle className="w-4 h-4 text-rose-500" />
                                  : <Icon className="w-4 h-4" style={{ color: cat.color }} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold">{s.title}</span>
                            <span className="text-[9px] font-bold uppercase tracking-wide rounded-full px-1.5 py-0.5" style={{ color: cat.color, background: `${cat.color}1a` }}>{cat.label}</span>
                          </div>
                          <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-0.5 leading-snug">
                            {s.status === "done" || s.status === "failed" || s.status === "nochange" ? (s.summary || s.rationale) : s.rationale}
                          </p>
                          {s.version != null && s.status === "done" && (
                            <span className="text-[10px] text-emerald-500 font-semibold">✓ saved as v{s.version}</span>
                          )}
                        </div>
                        {phase === "plan" && (
                          <div className="flex items-center gap-1 shrink-0">
                            <button onClick={() => openPreview(s)} title="Preview this step" className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid={`autopilot-step-preview-${i}`}><Eye className="w-4 h-4" /></button>
                            <button onClick={() => move(i, -1)} disabled={i === 0} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] disabled:opacity-30" data-testid={`autopilot-step-up-${i}`}><ChevronUp className="w-4 h-4" /></button>
                            <button onClick={() => move(i, 1)} disabled={i === steps.length - 1} className="text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] disabled:opacity-30" data-testid={`autopilot-step-down-${i}`}><ChevronDown className="w-4 h-4" /></button>
                            <Switch checked={s.include} onCheckedChange={() => toggleInclude(s.id)} data-testid={`autopilot-step-toggle-${i}`} />
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Actions */}
              {phase === "plan" && (
                <div className="mt-4 space-y-3">
                  {sharedFrom && !planId && (
                    <div className="rounded-xl border border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 px-3 py-2.5 flex items-start gap-2" data-testid="autopilot-shared-banner">
                      <Share2 className="w-4 h-4 text-[hsl(var(--primary))] mt-0.5 shrink-0" />
                      <p className="text-[11px] leading-snug">You're viewing a shared plan from <b>{sharedFrom}</b>. Run it as-is, or save it as your own to tweak the steps and re-share.</p>
                    </div>
                  )}
                  <div className="flex items-center justify-between rounded-xl bg-[hsl(var(--secondary))]/40 px-3 py-2.5">
                    <span className="text-sm font-medium flex items-center gap-1.5"><Bug className="w-4 h-4 text-[hsl(var(--primary))]" /> Auto-playtest when done</span>
                    <Switch checked={autoQa} onCheckedChange={setAutoQa} data-testid="autopilot-autoqa-toggle" />
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={runAutopilot} className="rounded-full flex-1 bg-gradient-to-r from-[#1f6feb] to-[#a855f7]" data-testid="autopilot-run-btn">
                      <Wand2 className="w-4 h-4" /> Run Autopilot · {includedCount} {includedCount === 1 ? "step" : "steps"} <span className="opacity-70 text-xs">({includedCount} cr)</span>
                    </Button>
                    {sharedFrom && !planId && (
                      <Button variant="outline" onClick={adoptPlan} disabled={adoptBusy} className="rounded-full shrink-0" title="Save this shared plan as your own so you can tweak & re-share it" data-testid="autopilot-adopt-btn">
                        {adoptBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save as mine</>}
                      </Button>
                    )}
                    {planId && (
                      <Button variant="outline" onClick={sharePlan} disabled={shareBusy} className="rounded-full shrink-0" title="Share this plan so a friend can rerun it on their own game" data-testid="autopilot-share-btn">
                        {shareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}
                      </Button>
                    )}
                    <Button variant="outline" onClick={reset} className="rounded-full" data-testid="autopilot-reset-btn"><RotateCcw className="w-4 h-4" /></Button>
                  </div>
                </div>
              )}

              {phase === "running" && (
                <p className="text-xs text-center text-[hsl(var(--muted-foreground))] mt-4">
                  <Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1" /> Building your game — watch the preview update live →
                </p>
              )}

              {phase === "done" && (
                <div className="mt-4 space-y-3">
                  {qa && (
                    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--background))] p-3 flex items-center gap-3" data-testid="autopilot-qa">
                      {qa.status !== "ready" ? (
                        <><Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--primary))]" /><span className="text-sm text-[hsl(var(--muted-foreground))]">Running an AI playtest on your upgraded game…</span></>
                      ) : (
                        <>
                          <div className="grid place-items-center w-11 h-11 rounded-full shrink-0" style={{ background: `conic-gradient(#34d399 ${qa.overall * 3.6}deg, hsl(var(--secondary)) 0deg)` }}>
                            <div className="w-8 h-8 rounded-full bg-[hsl(var(--background))] grid place-items-center text-xs font-bold" data-testid="autopilot-qa-score">{qa.overall}</div>
                          </div>
                          <div className="min-w-0"><p className="text-xs font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">New playtest score</p><p className="text-sm font-medium leading-snug">{qa.verdict}</p></div>
                        </>
                      )}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={() => navigate(`/app/build/${gameId}`)} className="rounded-full flex-1" data-testid="autopilot-open-builder">
                      <PencilRuler className="w-4 h-4" /> Open in builder
                    </Button>
                    {!qa && (
                      <Button variant="outline" onClick={runQa} className="rounded-full" data-testid="autopilot-run-qa">
                        <Bug className="w-4 h-4" /> Run playtest
                      </Button>
                    )}
                    {planId && (
                      <Button variant="outline" onClick={sharePlan} disabled={shareBusy} className="rounded-full" data-testid="autopilot-share-done-btn">
                        {shareBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />} Share plan
                      </Button>
                    )}
                    <Button variant="outline" onClick={reset} className="rounded-full" data-testid="autopilot-new-goal">
                      <Sparkles className="w-4 h-4" /> New goal
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Right: live preview */}
        <div className="lg:sticky lg:top-6">
          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4">
            <div className="flex items-center gap-2 mb-3 text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
              <Play className="w-3.5 h-3.5 text-[hsl(var(--primary))]" /> Live preview
              {phase === "running" && <span className="text-[hsl(var(--primary))] normal-case font-medium">· updating…</span>}
            </div>
            {config ? <GameCanvas config={config} compact previewMode /> : <div className="aspect-video grid place-items-center"><Spinner /></div>}
            <p className="text-[11px] text-[hsl(var(--muted-foreground))] mt-3 text-center">
              Every step is saved as an undoable version — tweak anything later in the builder.
            </p>
          </div>
        </div>
      </div>

      {/* Step diff preview (before → after) */}
      <Dialog open={!!preview} onOpenChange={(v) => { if (!v && !applying) setPreview(null); }}>
        <DialogContent className="max-w-2xl" data-testid="autopilot-preview-dialog">
          <DialogHeader>
            <DialogTitle className="text-base flex items-center gap-2">
              <Eye className="w-4 h-4 text-[hsl(var(--primary))]" /> Preview: {preview?.step?.title}
            </DialogTitle>
          </DialogHeader>
          {!preview?.data ? (
            <div className="h-64 grid place-items-center">
              <div className="text-center">
                <Loader2 className="w-6 h-6 animate-spin text-[hsl(var(--primary))] mx-auto" />
                <p className="text-xs text-[hsl(var(--muted-foreground))] mt-2">Simulating this change…</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm" data-testid="autopilot-preview-summary">{preview.data.summary}</p>
              <div className="inline-flex rounded-full border border-[hsl(var(--border))] p-0.5 text-xs">
                <button onClick={() => setPreview((p) => ({ ...p, side: "before" }))}
                  className={`px-3 py-1 rounded-full transition-colors ${preview.side === "before" ? "bg-[hsl(var(--secondary))] font-semibold" : "text-[hsl(var(--muted-foreground))]"}`}
                  data-testid="autopilot-preview-before">Before</button>
                <button onClick={() => setPreview((p) => ({ ...p, side: "after" }))}
                  className={`px-3 py-1 rounded-full transition-colors ${preview.side === "after" ? "bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] font-semibold" : "text-[hsl(var(--muted-foreground))]"}`}
                  data-testid="autopilot-preview-after">After</button>
              </div>
              <div className="rounded-xl overflow-hidden border border-[hsl(var(--border))]">
                <GameCanvas key={preview.side} config={preview.side === "before" ? preview.data.before : preview.data.after} compact previewMode />
              </div>
              <div className="flex gap-2">
                <Button onClick={applyPreview} disabled={applying || !preview.data.changed} className="rounded-full flex-1 bg-gradient-to-r from-[#1f6feb] to-[#a855f7]" data-testid="autopilot-preview-apply">
                  {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Apply this step
                </Button>
                <Button variant="outline" onClick={() => setPreview(null)} disabled={applying} className="rounded-full" data-testid="autopilot-preview-close">Discard</Button>
              </div>
              {!preview.data.changed && <p className="text-[11px] text-[hsl(var(--muted-foreground))] text-center">This step wouldn't change the game — you can skip it.</p>}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
